"use client"

import { useEffect, useRef, useCallback, useState } from "react"
import { createBrowserClient } from "@forestsync/auth/browser"
import type { TradeFill } from "@/components/trading/TradeBlotter"
import { getTradeHistorySinceAction } from "@/actions/trading"

/**
 * Subscribes to Supabase Realtime for filled trade orders on a specific market.
 * Returns an array of newly detected fills since mount.
 * Consumers should append these to their existing fills state.
 */
export function useRealtimeTrades(
  creditType: string,
  vintage: number,
  projectRefId: string,
  initial: TradeFill[],
): TradeFill[] {
  const supabaseRef = useRef(createBrowserClient())
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Stable ref so effect deps don't include the initial array (avoids infinite re-render)
  const initialRef = useRef(initial)
  initialRef.current = initial
  // Track the timestamp of the latest fill we've seen so we only fetch truly new ones
  const lastSeenRef = useRef<Date>(
    initial.length > 0
      ? new Date(initial[0].time.getTime() + 1)
      : new Date(Date.now() - 5_000),
  )
  const [newFills, setNewFills] = useState<TradeFill[]>([])

  const fetchNew = useCallback(async () => {
    try {
      const rows = await getTradeHistorySinceAction(
        creditType,
        vintage,
        projectRefId,
        lastSeenRef.current.toISOString(),
      )
      const fills: TradeFill[] = rows
        .filter((r) => r.filledAt !== null)
        .map((r) => ({
          id: r.refId,
          time: new Date(r.filledAt!),
          side: r.side,
          price: Number(r.price ?? 0),
          volume: Number(r.volume),
          transactionRefId: r.transactionRefId ?? undefined,
          aggressorSide: r.aggressorSide ?? null,
          optionContractId: r.optionContractId ?? null,
        }))
      if (fills.length > 0) {
        const latest = fills.reduce(
          (m, f) => (f.time > m ? f.time : m),
          fills[0].time,
        )
        lastSeenRef.current = new Date(latest.getTime() + 1)
        setNewFills(fills)
      }
    } catch {
      // Silently ignore — keep last good data
    }
  }, [creditType, vintage, projectRefId])

  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchNew, 500)
  }, [fetchNew])

  useEffect(() => {
    // Reset on market change — use ref to avoid initial array in deps (infinite loop)
    lastSeenRef.current =
      initialRef.current.length > 0
        ? new Date(initialRef.current[0].time.getTime() + 1)
        : new Date(Date.now() - 5_000)
    setNewFills([])

    const channel = supabaseRef.current
      .channel(`trades:${creditType}:${vintage}:${projectRefId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "trade_orders",
          filter: `credit_type=eq.${creditType}`,
        },
        (payload) => {
          // For DELETE events payload.new is {} (empty object, not null), so fall back to payload.old
          const raw = payload.new as Record<string, unknown> | undefined
          const row = (raw && Object.keys(raw).length > 0 ? raw : payload.old) as Record<string, unknown> | undefined
          if (!row) return
          if (Number(row.vintage) !== vintage) return
          if (row.project_ref_id !== projectRefId) return
          // Only react to filled / partial orders (status is an integer from Supabase: 2=PARTIAL, 3=FILLED)
          const status = Number(row.status)
          if (status !== 2 && status !== 3) return
          debouncedFetch()
        },
      )
      .subscribe()

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      supabaseRef.current.removeChannel(channel)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditType, vintage, projectRefId, debouncedFetch])

  return newFills
}
